// oneThing.js — the Home header's "One thing" line, computed LIVE from state
// (2026-10-22), never from the Kai brief (which goes stale after a log).
// First match wins:
//   1. next open commitment later today        "Gym at 6:00 PM"
//   2. unpaid, non-autopay bill due today/tomorrow  "Internet due tomorrow"
//   3. a weekly goal behind pace today can still move  "1 workout left this week"
//      (skipped when today is already logged for it)
//   4. after noon, habits not logged today     "2 habits left today" / "Read 20 min left today"
//   5. every habit done and nothing above      "All done today" (no prefix)
//   else null (the line is hidden).
// One line, at most ~40 characters, ellipsis beyond. Pure, unit-tested.
import { weeklyCountStatus } from './goalPace.js';

export const ONE_THING_MAX = 40;
const NOON = 12 * 60;
const clockToMinutes = (t) => { const m = /^(\d{1,2}):(\d{2})/.exec(String(t || '')); return m ? (+m[1]) * 60 + (+m[2]) : null; };

export function truncateOneThing(text, max = ONE_THING_MAX) {
  const s = String(text || '').replace(/\s+/g, ' ').trim();
  if (s.length <= max) return s;
  const cut = s.slice(0, max - 1);
  const sp = cut.lastIndexOf(' ');
  return `${(sp > max * 0.6 ? cut.slice(0, sp) : cut).replace(/[,:;]$/, '')}…`;
}

const fmtLeft = (goal, remaining) => {
  const u = String(goal.unit || '').toLowerCase();
  if (u === 'workouts' || u === '') return `${remaining} workout${remaining === 1 ? '' : 's'} left this week`;
  if (u === 'mi') return `${Number(remaining.toFixed(1))} mi left this week`;
  if (u === 'steps') return `${Math.round(remaining).toLocaleString('en-US')} steps left this week`;
  return `${goal.title}: ${Number(remaining.toFixed(1))} ${goal.unit} left this week`;
};

/**
 * @param {object} s
 * @param {string} s.todayStr            local date
 * @param {number} s.nowMinutes          local minutes since midnight
 * @param {Array}  s.commitments         [{ status, due_date, due_time, text, metric_ref }]
 * @param {Array}  s.bills               annotateBills output [{ name, daysUntil, autopay, paidThisMonth }]
 * @param {Array}  s.goals               weekly goals [{ title, current, target, unit, inverted, hasData, loggedToday }]
 * @param {Array}  s.habits              [{ label, loggedToday, active }]
 * @param {Function} [s.commitLabel]
 * @param {Function} [s.fmt12h]
 * @returns {{ text: string, prefix: boolean } | null}
 */
export function computeOneThing(s = {}) {
  const { todayStr, nowMinutes = 0, commitLabel = (c) => c?.text || '', fmt12h = (t) => t } = s;
  const out = (text, prefix = true) => ({ text: truncateOneThing(text), prefix });

  // 1. the next open commitment later today
  const later = (s.commitments || [])
    .filter(c => c && c.status === 'open' && c.due_date === todayStr && clockToMinutes(c.due_time) != null && clockToMinutes(c.due_time) > nowMinutes)
    .sort((a, b) => clockToMinutes(a.due_time) - clockToMinutes(b.due_time))[0];
  if (later) return out(`${commitLabel(later)} at ${fmt12h(later.due_time)}`);

  // 2. a bill the user must pay, due today or tomorrow
  const bill = (s.bills || []).filter(b => b && !b.paidThisMonth && b.autopay !== true && (b.daysUntil === 0 || b.daysUntil === 1)).sort((a, b) => a.daysUntil - b.daysUntil)[0];
  if (bill) return out(`${bill.name} due ${bill.daysUntil === 0 ? 'today' : 'tomorrow'}`);

  // 3. a weekly goal behind pace that today can still move
  for (const g of s.goals || []) {
    if (!g || g.inverted || g.hasData === false || g.loggedToday) continue;
    const target = Number(g.target), current = Number(g.current) || 0;
    if (!(target > 0) || current >= target) continue;
    const status = weeklyCountStatus({ current, target, today: todayStr });
    if (status === 'behind' || status === 'at_risk') return out(fmtLeft(g, target - current));
  }

  // 4. after noon, habits still to log
  const active = (s.habits || []).filter(h => h && h.active !== false);
  const open = active.filter(h => !h.loggedToday);
  if (nowMinutes >= NOON && open.length) return out(open.length === 1 ? `${open[0].label || 'Habit'} left today` : `${open.length} habits left today`);

  // 5. everything done
  if (active.length && !open.length) return out('All done today', false);
  return null;
}
